import { AlertRulesService } from './services/alert-rules.service';
import { BrowserModule } from '@angular/platform-browser';
import { CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import { RouterLink, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  HttpClientModule,
} from '@angular/common/http';
import { AppRoutes } from './app.routing';
import { AppComponent } from './app.component';
import { DecimalPipe } from '@angular/common';
import { FullComponent } from './layouts/full/full.component';
import { AppBlankComponent } from './layouts/blank/blank.component';
import { AppSidebarComponent } from './layouts/full/sidebar/sidebar.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { MaterialModule } from './material-module';
import {
  PERFECT_SCROLLBAR_CONFIG,
  PerfectScrollbarConfigInterface,
  PerfectScrollbarModule,
} from 'ngx-perfect-scrollbar';
import { SharedModule } from './shared/shared.module';
import { SpinnerComponent } from './shared/spinner.component';
import { StationService } from './services/station.service';
import { ReservedTimesService } from './services/reserved-times.service';
import { AuthGuard } from './guards/auth.guard';
import { Globals } from './shared/globals';
import {
  TranslateLoader,
  TranslateModule,
  TranslateService,
} from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { ModelHelperService } from './services/model-helper.service';
import { SpecialUserService } from './services/special-user.service';
import { OverlayContainer } from '@angular/cdk/overlay';
import { DateHelperService } from './services/date-helper.service';
import { TypesService } from './services/types.service';
import { AuthService } from './services/auth.service';
import { PaymentsService } from './services/payment.service';
import { HistoryService } from './services/history.service';
import { PayoutService } from './services/payout.service';
import { MoneyAccountService } from './services/money-account.service';
import { OcppSettingsService } from './services/ocpp-settings.service';
import { QrCodesService } from './services/qr-codes.service';
import { StationSubtypeService } from './services/station-subtype.service';
import { RfidCardsService } from './services/rfidcards.service';
import { CardTerminalService } from './services/card-terminal.service';
import { UserGroupService } from './services/user-group.service';
import { StationGroupService } from './services/station-group.service';
import { PermissionService } from './services/permission.service';
import { LocalizationHelper } from './groups/shared/helpers/localization-helper';
import { MaintenanceService } from './services/maintenance.service';
import { ActivationService } from './services/activation-service';
import { ReportsService } from './services/reports.service';
import { FeatureService } from './services/feature.service';
import { HubjectService } from './services/hubject.service';
import { AdminService } from './services/admin.service';
import { MatLegacyPaginatorIntl as MatPaginatorIntl } from '@angular/material/legacy-paginator';
import { MatPaginatorI18nService } from './services/mat-paginator-i18n.service';
import { DocumentVisibilityInterceptor } from './shared/http-interceptors/document-visibility.interceptor';
import { ApiErrorParser } from './shared/apiErrorParser';
import { AccountService } from './services/account.service';
import { UserGroupsBulkUserAddService } from './services/user-groups-bulk-user-add-service';
import { Api } from './shared/api';
import { AdminCarService } from './services/admin-car.service';
import { LoadmanagementService } from './services/loadmanagement.service';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatLegacyFormFieldModule as MatFormFieldModule } from '@angular/material/legacy-form-field';
import { MatLegacyInputModule as MatInputModule } from '@angular/material/legacy-input';
import {
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
  MatNativeDateModule,
} from '@angular/material/core';
import { StatisticsService } from './services/statistics.service';
import { OcpiPartnerService } from './services/ocpi-partner.service';

const DEFAULT_PERFECT_SCROLLBAR_CONFIG: PerfectScrollbarConfigInterface = {
  suppressScrollX: true,
  wheelSpeed: 2,
  wheelPropagation: true,
};

export function createTranslateLoader(http: HttpClient): TranslateHttpLoader {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

export const MY_DATE_FORMATS = {
  parse: {
    dateInput: 'DD/MM/YYYY',
  },
  display: {
    dateInput: 'DD/MM/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'DD/MM/YYYY',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

@NgModule({
  declarations: [
    AppComponent,
    FullComponent,
    SpinnerComponent,
    AppBlankComponent,
    AppSidebarComponent,
  ],
  imports: [
    HttpClientModule,
    BrowserModule,
    BrowserAnimationsModule,
    MaterialModule,
    FormsModule,
    PerfectScrollbarModule,
    SharedModule,
    RouterModule.forRoot(AppRoutes, {}),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpClient],
      },
    }),
    MatDatepickerModule,
    MatFormFieldModule,
    MatInputModule,
    MatNativeDateModule,
    RouterLink,
  ],
  providers: [
    {
      provide: PERFECT_SCROLLBAR_CONFIG,
      useValue: DEFAULT_PERFECT_SCROLLBAR_CONFIG,
    },
    {
      provide: MatPaginatorIntl,
      useClass: MatPaginatorI18nService,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: DocumentVisibilityInterceptor,
      multi: true,
    },
    StationService,
    OcppSettingsService,
    QrCodesService,
    StationSubtypeService,
    ReservedTimesService,
    SpecialUserService,
    ModelHelperService,
    DateHelperService,
    TypesService,
    AuthService,
    AuthGuard,
    Globals,
    DecimalPipe,
    PaymentsService,
    HistoryService,
    MaintenanceService,
    PayoutService,
    MoneyAccountService,
    TranslateService,
    RfidCardsService,
    CardTerminalService,
    UserGroupService,
    StationGroupService,
    PermissionService,
    LocalizationHelper,
    ActivationService,
    ReportsService,
    LocalizationHelper,
    FeatureService,
    LocalizationHelper,
    HubjectService,
    AdminService,
    AdminCarService,
    ApiErrorParser,
    AccountService,
    UserGroupsBulkUserAddService,
    AlertRulesService,
    Api,
    LoadmanagementService,
    { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS },
    { provide: MAT_DATE_LOCALE, useValue: 'en-GB' },
    StatisticsService,
    OcpiPartnerService,
  ],
  bootstrap: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppModule {
  constructor(overlayContainer: OverlayContainer) {
    overlayContainer.getContainerElement().classList.add('ecarup');
  }
}
